'use client';
import { Button } from '@ahafood/ui/components/button';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  findPairedBanner,
  getCampaignBannersFn,
  pickImageUrl,
  type CampaignBanner,
} from '@/api/ahafood/campaign-banner';
import { useRouter } from '@/i18n/routing';

type Props = {
  campaignId?: string;
  city?: string;
};

export default function CampaignSession({ campaignId, city }: Props) {
  const router = useRouter();
  const [banners, setBanners] = useState<CampaignBanner[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const result = await getCampaignBannersFn({
          city_id: city ?? '',
          screen: 'home',
        });
        setBanners(result);
      } catch (error) {
        setBanners(null);
      } finally {
        setLoading(false);
      }
    };
    void fetchBanners();
  }, [city]);

  if (loading) {
    return (
      <div className="mb-6 animate-pulse">
        <div className="mb-4 flex items-center gap-3">
          <div className="size-9 shrink-0 rounded-full bg-neutral-20" />
          <div className="h-6 w-1/2 rounded bg-neutral-20" />
        </div>
        <div className="aspect-[21/9] w-full rounded-2xl bg-neutral-20" />
        <div className="mt-4 h-4 w-2/3 rounded bg-neutral-20" />
      </div>
    );
  }

  const banner = banners?.find((b) => b._id === Number(campaignId));

  if (!banner) {
    return (
      <div className="mb-6 flex flex-col items-center justify-center rounded-2xl border border-neutral-20 bg-white/70 py-16 text-center">
        <h1 className="text-xl font-bold md:text-2xl">
          Chiến dịch không tồn tại
        </h1>
        <p className="mt-2 text-sm text-neutral-60">Vui lòng thử lại sau.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => router.back()}
        >
          <ArrowLeft className="mr-1 size-4" /> Quay lại
        </Button>
      </div>
    );
  }

  // Hero = banner ngang 21:9 (AF-063): trang mở theo `_id` của SUPER_DEAL (ảnh
  // vuông), banner ngang nằm ở FEATURED_BANNER cùng chiến dịch. Ảnh AI đã in
  // sẵn tên + logo, nên tiêu đề và nút back nằm ở hàng riêng phía trên — đè lên
  // ảnh thì vừa lặp chữ vừa che logo.
  const wide =
    banner.type === 'FEATURED_BANNER'
      ? banner
      : findPairedBanner(banner, banners ?? [], 'FEATURED_BANNER');
  const wideSrc = pickImageUrl(wide?.image_url, banner.background_image_url);
  // Không ghép được (chiến dịch cũ, ảnh tự tải lên) → chỉ còn ảnh vuông của
  // chính banner: đặt nguyên ảnh giữa khung trên nền mờ, crop 21:9 là mất chữ.
  const heroSrc =
    wideSrc ??
    pickImageUrl(banner.image_url) ??
    '/static/images/empty-store.webp';

  return (
    <div className="mb-6">
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => router.back()}
          aria-label="Quay lại"
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-neutral-80 shadow-sm ring-1 ring-black/5 transition hover:bg-neutral-10"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="line-clamp-2 text-xl font-bold text-neutral-90 md:text-2xl">
          {banner.title_vi_vn}
        </h1>
      </div>
      <div className="relative aspect-[21/9] w-full overflow-hidden rounded-2xl bg-neutral-10 shadow-sm ring-1 ring-black/5">
        {wideSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={heroSrc}
            alt={banner.title_vi_vn}
            className="size-full object-cover"
          />
        ) : (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={heroSrc}
              alt=""
              aria-hidden
              className="absolute inset-0 size-full scale-110 object-cover opacity-60 blur-2xl"
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={heroSrc}
              alt={banner.title_vi_vn}
              className="relative mx-auto h-full w-auto object-contain"
            />
          </>
        )}
      </div>
      {banner.description_vi_vn && (
        <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-neutral-70 md:text-base">
          {banner.description_vi_vn}
        </p>
      )}
    </div>
  );
}
